import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Tag } from '@/components/ui/tag';
import { AlertTriangle, X, Edit2, Edit, Save, Clock, Target, Loader2, RefreshCw, Trash2, Plus, Minus, Users, Info, ChefHat } from 'lucide-react';
import { DeleteMealSlotOptions, MealSlot, FoodItem, MealType, RecipeServingSize } from '@/types/meal-plan';
import { getMealColor } from '@/data/meal-plan-config';
import {
  addManualMealPlanItem,
  removeManualMealPlanItem,
  updateMealSlot,
  searchMealPlanItems,
  updateManualMealPlanServings,
  updateManualMealPlanIngredient,
  getManualMealPlanDraft,
  getIngredientDetails,
  getIngredientsAutocomplete,
  getIngredientNutrition,
  getIngredientNutritionByIngredientId,
  getIngredientNutritionByRecipeIngredientId,
} from '@/lib/apiService';
import { SearchableDropdown, RecipeSearchResult } from '@/components/shared/SearchableDropdown';
import { useDebounce } from '@/hooks/useDebounce';
import { showErrorToast, showWarningToast } from '@/utils/toast-utils';
import { v4 as uuidv4 } from 'uuid';
import RecipeDetailsModal from '@/components/meal-planning/RecipeDetailsModal';
import IngredientSearchModal from '@/components/meal-planning/IngredientSearchModal';
import ManualPlanConfirmDialog from '@/components/meal-planning/manual/ManualPlanConfirmDialog';
import RecipeServingInfoPopover from '@/components/meal-planning/RecipeServingInfoPopover';
import { RecipeAllergenCard } from '@/components/recipes/RecipeAllergenCard';
import { SpoonacularTagSummary } from '@/components/recipes/SpoonacularTagSummary';
import { formatRecipeSource } from '@/lib/utils';
import { extractIngredientUnitMetadata, normalizeIngredientUnitKey } from '@/utils/ingredient-units';
import { roundNutritionDisplay } from '@/utils/nutrition-display-format';

// Utility function to convert 24-hour time to 12-hour format
const formatTimeTo12Hour = (time24: string): string => {
  if (!time24) return '';
  
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const minute = minutes || '00';
  
  if (hour === 0) {
    return `12:${minute} AM`;
  } else if (hour < 12) {
    return `${hour}:${minute} AM`;
  } else if (hour === 12) {
    return `12:${minute} PM`;
  } else {
    return `${hour - 12}:${minute} PM`;
  }
};

const formatUnitForDisplay = (unit?: string): string => {
  if (!unit) return '';
  return unit.replace(/_/g, ' ');
};

const formatNumber = (value: number): string =>
  !Number.isFinite(value) ? '0' : roundNutritionDisplay(Math.max(0, value));

const formatKcalValue = (value: number): string =>
  !Number.isFinite(value) ? '0' : roundNutritionDisplay(Math.max(0, value));

const formatPerPersonShare = (totalYield: number): string => {
  if (!Number.isFinite(totalYield) || totalYield <= 0) return '1';

  const roundedYield = Math.round(totalYield * 100) / 100;
  if (Number.isInteger(roundedYield)) {
    const whole = Math.trunc(roundedYield);
    if (whole > 1 && whole <= 12) {
      return `1/${whole}`;
    }
  }

  const share = 1 / roundedYield;
  const roundedShare = Math.round(share * 100) / 100;
  return Number.isInteger(roundedShare) ? String(roundedShare) : String(roundedShare);
};

const toFiniteNumber = (value: unknown): number => {
  const parsed = typeof value === 'string' ? Number(value) : value;
  return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : 0;
};

const normalizeStringList = (value: unknown): string[] => (
  Array.isArray(value)
    ? value.map((entry) => String(entry ?? '').trim()).filter(Boolean)
    : []
);

const normalizeManualProvider = (
  provider: unknown,
  source: unknown
): 'spoonacular' | 'edamam' | 'internal' => {
  if (provider === 'internal' || provider === 'spoonacular' || provider === 'edamam') {
    return provider;
  }
  if (provider === 'custom') return 'internal';
  if (source === 'cached' || source === 'custom' || source === 'internal' || source === 'manual') return 'internal';
  if (source === 'edamam') return 'edamam';
  return 'spoonacular';
};

const MONGO_OBJECT_ID_REGEX = /^[a-f0-9]{24}$/i;
const SPOONACULAR_PREFIX = 'spoonacular_';
const MIN_RECIPE_NUTRITION_SERVINGS = 0.1;
const MAX_RECIPE_NUTRITION_SERVINGS = 20;

const normalizeIngredientLookupId = (rawId?: string): string => {
  const id = String(rawId ?? '').trim();
  if (!id) return '';
  if (id.startsWith(SPOONACULAR_PREFIX)) return id;
  if (/^\d+$/.test(id)) return `${SPOONACULAR_PREFIX}${id}`;
  return id;
};

const resolveIngredientLookupId = (food: FoodItem): string => {
  const normalizedId = normalizeIngredientLookupId(food.id);
  const normalizedSourceId = normalizeIngredientLookupId(food.sourceId);

  if (MONGO_OBJECT_ID_REGEX.test(normalizedSourceId) || normalizedSourceId.startsWith(SPOONACULAR_PREFIX)) {
    return normalizedSourceId;
  }
  if (MONGO_OBJECT_ID_REGEX.test(normalizedId) || normalizedId.startsWith(SPOONACULAR_PREFIX)) {
    return normalizedId;
  }

  return normalizedSourceId || normalizedId;
};

const resolveRecipeIngredientId = (food: FoodItem): string => {
  const normalizedId = String(food.id ?? '').trim();
  return MONGO_OBJECT_ID_REGEX.test(normalizedId) ? normalizedId : '';
};

const buildIngredientUnitOptions = (
  units: string[],
  labelsByKey: Record<string, string>,
  currentUnit: string,
): Array<{ value: string; label: string }> => {
  const mergedUnits = [...new Set([currentUnit, ...units].filter(Boolean).map((unit) => String(unit).trim()))];
  return mergedUnits.map((unit) => ({
    value: unit,
    label: labelsByKey[normalizeIngredientUnitKey(unit)] ?? formatUnitForDisplay(unit),
  }));
};

const parseRecipeServingValue = (value: unknown): number | null => {
  const parsed = typeof value === 'string' ? Number(value) : value;
  if (typeof parsed !== 'number' || !Number.isFinite(parsed)) {
    return null;
  }
  const rounded = Math.round(parsed * 100) / 100;
  if (rounded < MIN_RECIPE_NUTRITION_SERVINGS || rounded > MAX_RECIPE_NUTRITION_SERVINGS) {
    return null;
  }
  return rounded;
};

const parseRecipeServingId = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  return normalized.length > 0 ? normalized : null;
};

const normalizeRecipeServingSizes = (recipe: RecipeSearchResult): RecipeServingSize[] => {
  const raw = Array.isArray((recipe as any)?.recipe?.servingSizes)
    ? (recipe as any).recipe.servingSizes
    : Array.isArray((recipe as any)?.servingSizes)
      ? (recipe as any).servingSizes
      : [];

  const seen = new Set<string>();
  const normalized: RecipeServingSize[] = [];
  for (const entry of raw) {
    const servingId = parseRecipeServingId(entry?.servingId);
    const grams = Number(entry?.grams);
    if (!servingId || !Number.isFinite(grams) || grams <= 0 || seen.has(servingId)) continue;
    seen.add(servingId);
    normalized.push({
      servingId,
      grams: Math.round(grams * 100) / 100,
      label: typeof entry?.label === 'string' && entry.label.trim().length > 0 ? entry.label.trim() : null,
      isDefault: entry?.isDefault === true,
    });
  }
  return normalized;
};

const buildFallbackAllowedRecipeServings = (servings: unknown): number[] => {
  const parsedServings = Number(servings);
  const maxServings = Number.isFinite(parsedServings) && parsedServings > 0
    ? Math.min(Math.round(parsedServings), MAX_RECIPE_NUTRITION_SERVINGS)
    : 1;
  return Array.from({ length: maxServings }, (_, index) => index + 1);
};

const getRecipeAllowedServings = (recipe: RecipeSearchResult): number[] => {
  const allowedRaw = Array.isArray(recipe.allowedServings)
    ? recipe.allowedServings
    : Array.isArray((recipe as any)?.recipe?.allowedServings)
      ? (recipe as any).recipe.allowedServings
      : [];

  const parsedAllowed = allowedRaw
    .map((value) => parseRecipeServingValue(value))
    .filter((value): value is number => value !== null);

  if (parsedAllowed.length === 0) {
    return buildFallbackAllowedRecipeServings(recipe.servings);
  }

  return [...new Set<number>(parsedAllowed)].sort((a, b) => a - b);
};

const getRecipeDefaultServing = (recipe: RecipeSearchResult): number => {
  const defaultRaw = recipe.defaultServing ?? (recipe as any)?.recipe?.defaultServing;
  const parsedDefault = parseRecipeServingValue(defaultRaw);
  const allowed = getRecipeAllowedServings(recipe);

  if (parsedDefault !== null && allowed.includes(parsedDefault)) {
    return parsedDefault;
  }

  if (allowed.includes(1)) {
    return 1;
  }

  return allowed[0] ?? 1;
};

const getRecipeDefaultServingId = (recipe: RecipeSearchResult): string | null => {
  const servingSizes = normalizeRecipeServingSizes(recipe);
  const explicitDefault = parseRecipeServingId((recipe as any)?.recipe?.defaultServingId ?? (recipe as any)?.defaultServingId);
  if (servingSizes.length > 0) {
    if (explicitDefault && servingSizes.some((entry) => entry.servingId === explicitDefault)) {
      return explicitDefault;
    }
    return servingSizes.find((entry) => entry.isDefault)?.servingId ?? servingSizes[0]?.servingId ?? null;
  }
  return explicitDefault;
};

const formatServingOptionLabel = (value: number): string => {
  const normalized = Math.round(value * 100) / 100;
  return Number.isInteger(normalized) ? `${normalized}` : `${normalized}`;
};

const mapSearchResults = (results: any[]): RecipeSearchResult[] => {
  const nut = (item: any) => item?.nutrition ?? {};
  return results.map((item: any) => {
    const n = nut(item);
    const isRecipe = item.type === 'recipe';
    const provider = normalizeManualProvider(item.provider, item.source);
    const servingSizes: RecipeServingSize[] = isRecipe && Array.isArray(item?.recipe?.servingSizes)
      ? item.recipe.servingSizes
          .map((entry: any) => {
            const servingId = parseRecipeServingId(entry?.servingId);
            const grams = Number(entry?.grams);
            if (!servingId || !Number.isFinite(grams) || grams <= 0) return null;
            return {
              servingId,
              grams: Math.round(grams * 100) / 100,
              label: typeof entry?.label === 'string' && entry.label.trim().length > 0 ? entry.label.trim() : null,
              isDefault: entry?.isDefault === true,
            } as RecipeServingSize;
          })
          .filter((entry: RecipeServingSize | null): entry is RecipeServingSize => entry !== null)
      : [];
    const explicitDefaultServingId = parseRecipeServingId(item?.recipe?.defaultServingId);
    const defaultServingId = servingSizes.length > 0
      ? (
        explicitDefaultServingId && servingSizes.some((entry) => entry.servingId === explicitDefaultServingId)
          ? explicitDefaultServingId
          : servingSizes.find((entry) => entry.isDefault)?.servingId ?? servingSizes[0]?.servingId ?? null
      )
      : explicitDefaultServingId;
    const defaultServingFromList = defaultServingId
      ? servingSizes.find((entry) => entry.servingId === defaultServingId)
      : null;
    const defaultServingGramsRaw = Number(item?.recipe?.defaultServingGrams);
    const defaultServingGrams = defaultServingFromList?.grams
      ?? (Number.isFinite(defaultServingGramsRaw) && defaultServingGramsRaw > 0
        ? Math.round(defaultServingGramsRaw * 100) / 100
        : null);
    const recipeReadyInMinutes = isRecipe
      ? (() => {
          const direct = [
            item?.recipe?.readyInMinutes,
            item?.recipe?.totalTimeMinutes,
            item?.recipe?.totalMinutes,
          ];
          for (const candidate of direct) {
            const numeric = Number(candidate);
            if (Number.isFinite(numeric) && numeric > 0) return Math.round(numeric);
          }
          const prep = Number(item?.recipe?.prepTimeMinutes ?? item?.recipe?.prepMinutes);
          const cook = Number(item?.recipe?.cookTimeMinutes ?? item?.recipe?.cookMinutes);
          const total = (Number.isFinite(prep) && prep > 0 ? prep : 0) + (Number.isFinite(cook) && cook > 0 ? cook : 0);
          return total > 0 ? Math.round(total) : undefined;
        })()
      : undefined;
    return {
      id: item.id,
      title: item.name,
      name: item.name,
      description: item.description,
      image: item.imageUrl,
      readyInMinutes: recipeReadyInMinutes,
      servings: isRecipe ? item.recipe?.servings : undefined,
      defaultServing: isRecipe ? item.recipe?.defaultServing : undefined,
      allowedServings: isRecipe ? item.recipe?.allowedServings : undefined,
      defaultServingId: isRecipe ? defaultServingId : undefined,
      defaultServingGrams: isRecipe ? defaultServingGrams : undefined,
      servingSizes: isRecipe ? servingSizes : undefined,
      calories: n.calories,
      protein: n.protein,
      carbs: n.carbohydrate ?? n.carbs,
      fat: n.total_fat ?? n.fat,
      fiber: n.dietary_fibre ?? n.fiber,
      source: provider === 'internal' ? 'internal' : (provider === 'edamam' ? 'edamam' : 'spoonacular'),
      provider,
      sourceId: item.sourceId,
      spoonacularId: item.spoonacularId ?? item.spoonacularIngredientId,
      allergens: normalizeStringList(item.allergens ?? item.allergenTagIds),
      allergenTagIds: normalizeStringList(item.allergenTagIds ?? item.allergens),
      dietaryTagIds: normalizeStringList(item.dietaryTagIds ?? item.dietLabels ?? item.tags),
      dietLabels: normalizeStringList(item.dietLabels ?? item.dietaryTagIds ?? item.tags),
      intoleranceTagIds: normalizeStringList(item.intoleranceTagIds ?? item.intoleranceTags),
      intoleranceTags: normalizeStringList(item.intoleranceTags ?? item.intoleranceTagIds),
      tagClassificationStatus: item.tagClassificationStatus,
      tagClassificationSource: item.tagClassificationSource,
      tagClassificationUpdatedAt: item.tagClassificationUpdatedAt,
      clinicalWarnings: Array.isArray(item.clinicalWarnings) ? item.clinicalWarnings : [],
      isVegan: item.isVegan,
      isVegetarian: item.isVegetarian,
      isGlutenFree: item.isGlutenFree,
      isDairyFree: item.isDairyFree,
      healthLabels: item.isVegan ? ['Vegan'] : item.isVegetarian ? ['Vegetarian'] : [],
      // Keep unified API shape for selection (recipe vs ingredient)
      _searchItemType: item.type,
      _sourceId: item.sourceId,
      _ingredient: item.ingredient,
      _rawSource: item.source,
    };
  });
};

const normalizeSearchText = (value: string): string =>
  value
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const tokenizeSearchText = (value: string): string[] =>
  normalizeSearchText(value).split(' ').filter(Boolean);

const getSearchResultName = (item: RecipeSearchResult): string =>
  String(item.title ?? item.name ?? '').trim();

const calculateSearchMatchScore = (query: string, candidate: string): number => {
  const normalizedQuery = normalizeSearchText(query);
  const normalizedCandidate = normalizeSearchText(candidate);

  if (!normalizedQuery || !normalizedCandidate) return 0;

  let score = 0;

  if (normalizedCandidate === normalizedQuery) score += 1000;
  if (normalizedCandidate.startsWith(normalizedQuery)) score += 500;
  if (normalizedCandidate.includes(` ${normalizedQuery}`)) score += 250;
  if (normalizedCandidate.includes(normalizedQuery)) score += 150;

  const queryTokens = tokenizeSearchText(normalizedQuery);
  const candidateTokens = tokenizeSearchText(normalizedCandidate);

  for (const token of queryTokens) {
    if (candidateTokens.includes(token)) {
      score += 120;
      continue;
    }

    if (candidateTokens.some((candidateToken) => candidateToken.startsWith(token))) {
      score += 80;
      continue;
    }

    if (normalizedCandidate.includes(token)) {
      score += 40;
      continue;
    }

    score -= 20;
  }

  const firstToken = queryTokens[0];
  if (firstToken) {
    const firstTokenIndex = candidateTokens.findIndex((token) => token.startsWith(firstToken));
    if (firstTokenIndex === 0) {
      score += 60;
    } else if (firstTokenIndex > 0) {
      score += Math.max(0, 30 - firstTokenIndex * 5);
    }
  }

  score -= Math.min(100, Math.abs(normalizedCandidate.length - normalizedQuery.length));

  return score;
};

const rankSearchResults = (items: RecipeSearchResult[], query: string): RecipeSearchResult[] => {
  if (!items.length) return items;

  return [...items].sort((a, b) => {
    const scoreA = calculateSearchMatchScore(query, getSearchResultName(a));
    const scoreB = calculateSearchMatchScore(query, getSearchResultName(b));

    if (scoreB !== scoreA) return scoreB - scoreA;

    const nameA = normalizeSearchText(getSearchResultName(a));
    const nameB = normalizeSearchText(getSearchResultName(b));
    if (nameA.length !== nameB.length) return nameA.length - nameB.length;

    return 0;
  });
};

const dedupeSearchResults = (items: RecipeSearchResult[]): RecipeSearchResult[] => {
  const seenBySourceId = new Set<string>();
  const seenByNameType = new Set<string>();
  const deduped: RecipeSearchResult[] = [];

  for (const item of items) {
    const itemWithMeta = item as any;
    const itemType = String(itemWithMeta?._searchItemType ?? '').trim().toLowerCase();
    const sourceId = String(itemWithMeta?._sourceId ?? '').trim().toLowerCase();
    const normalizedName = String(item.title ?? item.name ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
    const fallbackId = String(item.id ?? '').trim().toLowerCase();
    const nameTypeKey = `${itemType}|name|${normalizedName || fallbackId}`;

    const isDuplicateBySourceId = !!sourceId && seenBySourceId.has(`${itemType}|sourceId|${sourceId}`);
    const isDuplicateByNameType = seenByNameType.has(nameTypeKey);

    if (isDuplicateBySourceId || isDuplicateByNameType) {
      continue;
    }

    if (sourceId) {
      seenBySourceId.add(`${itemType}|sourceId|${sourceId}`);
    }
    seenByNameType.add(nameTypeKey);
    deduped.push(item);
  }

  return deduped;
};

const MIN_CACHED_RESULTS_BEFORE_EXTERNAL_SEARCH = 5;

type MissingPreferenceMessage = {
  key: string;
  label: string;
  tooltip: string;
};

const pluralizeDescriptor = (descriptor: string) => {
  const normalized = descriptor.trim().toLowerCase();
  const pluralMap: Record<string, string> = {
    allergen: 'allergens',
    nut: 'nuts',
    'tree nut': 'tree nuts',
    peanut: 'peanuts',
    legume: 'legumes',
    egg: 'eggs',
  };

  return pluralMap[normalized] ?? normalized;
};

const formatMissingPreferenceMessage = (preference: string): MissingPreferenceMessage => {
  const trimmedPreference = preference?.trim();

  if (!trimmedPreference) {
    return {
      key: 'unverified',
      label: 'Might contain allergens',
      tooltip: "This recipe's allergen information is incomplete.",
    };
  }

  const normalizedPreference = trimmedPreference.toLowerCase();
  const hasFreeSuffix = normalizedPreference.includes('free');
  const descriptorBase = hasFreeSuffix
    ? normalizedPreference.replace(/-?free$/, '').replace(/free$/, '')
    : normalizedPreference;

  const descriptorSingular =
    descriptorBase
      .replace(/[_-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim() || 'allergen';

  const descriptorPlural = pluralizeDescriptor(descriptorSingular);
  const descriptorSlug = descriptorSingular.replace(/\s+/g, '-');

  const tooltipPreference = hasFreeSuffix
    ? `${descriptorSlug}-free`
    : descriptorSlug;

  const label = `Might contain ${descriptorPlural}`;

  const tooltip = hasFreeSuffix
    ? `This recipe may include ${descriptorPlural} ingredients or hasn't been verified as ${tooltipPreference}.`
    : `This recipe may include ${descriptorPlural} ingredients or hasn't been verified as ${tooltipPreference}.`;

  return {
    key: tooltipPreference,
    label,
    tooltip,
  };
};


interface MealSlotCardProps {
  slot: MealSlot;
  mealOrder?: number;
  selectedDay: number;
  onAddFood: (food: FoodItem, day: number, mealType: MealType, stateKey?: string) => void;
  onRemoveFood: (foodIndex: number, day: number, mealType: MealType, stateKey?: string) => void;
  onSwapFood?: (oldFood: FoodItem, newFood: FoodItem, day: number, mealType: MealType, stateKey?: string) => void;
  onUpdateMealInfo?: (day: number, mealType: MealType, updates: { name?: string; timeSlot?: string; targetKcal?: number | null }, stateKey?: string) => void;
  onDeleteMealSlot?: (day: number, mealName: string, options?: DeleteMealSlotOptions) => Promise<void>;
  onDraftDataUpdate?: (draftData: any) => void; // Callback to update draft data from API responses
  clientId?: string;
  cuisineTypes?: string[];
  draftId?: string;
  allergies?: string[];
  dietaryPreferences?: string[];
  isDeletingAnyMealSlot?: boolean;
}

const MealSlotCard: React.FC<MealSlotCardProps> = ({
  slot,
  mealOrder,
  selectedDay,
  onAddFood,
  onRemoveFood,
  onSwapFood,
  onUpdateMealInfo,
  onDeleteMealSlot,
  onDraftDataUpdate,
  clientId,
  cuisineTypes = [],
  draftId,
  allergies = [],
  dietaryPreferences = [],
  isDeletingAnyMealSlot = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(slot.name);
  const [editTimeSlot, setEditTimeSlot] = useState(slot.timeSlot);
  const [editTargetKcal, setEditTargetKcal] = useState<number | null>(slot.targetKcal ?? null);
  const [isDeletingSlot, setIsDeletingSlot] = useState(false);
  const [isUpdatingSlot, setIsUpdatingSlot] = useState(false);

  // Sync edit state when slot prop changes (e.g., from API updates)
  useEffect(() => {
    setEditName(slot.name);
    setEditTimeSlot(slot.timeSlot);
    setEditTargetKcal(slot.targetKcal);
  }, [slot.name, slot.timeSlot, slot.targetKcal]);
  
  // Recipe search state
  const [addingRecipeId, setAddingRecipeId] = useState<string | null>(null);
  const [recipeSearchTerm, setRecipeSearchTerm] = useState('');
  const [recipeSearchResults, setRecipeSearchResults] = useState<RecipeSearchResult[]>([]);
  const [recipeSearchSelectedServings, setRecipeSearchSelectedServings] = useState<Record<string, number>>({});
  const [recipeSearchSelectedServingIds, setRecipeSearchSelectedServingIds] = useState<Record<string, string>>({});
  const [isSearchingRecipes, setIsSearchingRecipes] = useState(false);
  const [showRecipeResults, setShowRecipeResults] = useState(false);
  const [isIngredientModalOpen, setIsIngredientModalOpen] = useState(false);
  
  const [hasAnyResults, setHasAnyResults] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Track if we just selected a recipe to prevent immediate re-search
  const justSelectedRecipeRef = useRef<string | null>(null);
  const lastSubmittedRecipeSearchRef = useRef<string | null>(null);
  // Ensure only the latest async search response can update UI state.
  const recipeSearchRequestIdRef = useRef(0);
  
  // API call runs only after 1s of no typing
  const debouncedRecipeSearch = useDebounce(recipeSearchTerm, 1000);
  const cuisineFilter = cuisineTypes.join(',');
  const allergiesFilter = allergies.join(',');
  const buildRecipeSearchParams = useCallback((rawQuery: string) => ({
    q: rawQuery.trim(),
    limit: 20,
    type: 'all' as const,
    cuisine: cuisineFilter || undefined,
    allergies: allergiesFilter || undefined,
    clientId: clientId || undefined,
  }), [allergiesFilter, clientId, cuisineFilter]);

  // Clear the just-selected flag when search term changes (user starts typing new search)
  useEffect(() => {
    if (justSelectedRecipeRef.current && recipeSearchTerm !== justSelectedRecipeRef.current) {
      justSelectedRecipeRef.current = null;
    }
    if (lastSubmittedRecipeSearchRef.current && recipeSearchTerm.trim() !== lastSubmittedRecipeSearchRef.current) {
      lastSubmittedRecipeSearchRef.current = null;
    }
  }, [recipeSearchTerm]);

  useEffect(() => {
    setRecipeSearchSelectedServings((prev) => {
      const next: Record<string, number> = {};

      for (const result of recipeSearchResults) {
        if ((result as any)?._searchItemType !== 'recipe') continue;

        const resultId = String(result.id || '').trim();
        if (!resultId) continue;

        const allowed = getRecipeAllowedServings(result);
        const defaultServing = getRecipeDefaultServing(result);
        const previousSelection = parseRecipeServingValue(prev[resultId]);

        next[resultId] = previousSelection !== null && allowed.includes(previousSelection)
          ? previousSelection
          : defaultServing;
      }

      return next;
    });
  }, [recipeSearchResults]);

  useEffect(() => {
    setRecipeSearchSelectedServingIds((prev) => {
      const next: Record<string, string> = {};

      for (const result of recipeSearchResults) {
        if ((result as any)?._searchItemType !== 'recipe') continue;

        const resultId = String(result.id || '').trim();
        if (!resultId) continue;

        const servingSizes = normalizeRecipeServingSizes(result);
        if (!servingSizes.length) continue;

        const defaultServingId = getRecipeDefaultServingId(result);
        const previousSelection = parseRecipeServingId(prev[resultId]);
        const selectedServingId = (
          previousSelection && servingSizes.some((entry) => entry.servingId === previousSelection)
            ? previousSelection
            : defaultServingId
        );

        if (selectedServingId) {
          next[resultId] = selectedServingId;
        }
      }

      return next;
    });
  }, [recipeSearchResults]);

  // Unified search: GET /meal-plans/search (recipes & ingredients)
  useEffect(() => {
    if (justSelectedRecipeRef.current && debouncedRecipeSearch === justSelectedRecipeRef.current) {
      recipeSearchRequestIdRef.current += 1;
      setShowRecipeResults(false);
      return;
    }

    if (lastSubmittedRecipeSearchRef.current === debouncedRecipeSearch.trim()) {
      return;
    }

    if (!clientId || !debouncedRecipeSearch || debouncedRecipeSearch.trim().length < 2) {
      recipeSearchRequestIdRef.current += 1;
      setRecipeSearchResults([]);
      setShowRecipeResults(false);
      setIsSearchingRecipes(false);
      setHasAnyResults(false);
      setIsLoadingMore(false);
      return;
    }

    const currentRequestId = recipeSearchRequestIdRef.current + 1;
    recipeSearchRequestIdRef.current = currentRequestId;

    const performSearch = async () => {
      const searchParams = buildRecipeSearchParams(debouncedRecipeSearch);

      setIsSearchingRecipes(true);
      setRecipeSearchResults([]);
      setShowRecipeResults(true);
      setHasAnyResults(false);
      setIsLoadingMore(false);

      try {
        const response = await searchMealPlanItems({
          ...searchParams,
          source: 'cached',
        });
        if (recipeSearchRequestIdRef.current !== currentRequestId) return;

        const cachedResults = rankSearchResults(
          dedupeSearchResults(mapSearchResults(response?.results ?? [])),
          searchParams.q
        );
        setRecipeSearchResults(cachedResults);
        if (cachedResults.length > 0) {
          setShowRecipeResults(true);
          setHasAnyResults(true);
        } else {
          setHasAnyResults(false);
        }

        if (cachedResults.length >= MIN_CACHED_RESULTS_BEFORE_EXTERNAL_SEARCH) {
          return;
        }

        if (cachedResults.length > 0) {
          setIsSearchingRecipes(false);
          setIsLoadingMore(true);
        }

        try {
          const externalResponse = await searchMealPlanItems({
            ...searchParams,
            source: 'all',
          });
          if (recipeSearchRequestIdRef.current !== currentRequestId) return;

          const externalResults = mapSearchResults(externalResponse?.results ?? []);
          const mergedResults = rankSearchResults(
            dedupeSearchResults([...cachedResults, ...externalResults]),
            searchParams.q
          );
          setRecipeSearchResults(mergedResults);
          setHasAnyResults(mergedResults.length > 0);
          if (mergedResults.length > 0) {
            setShowRecipeResults(true);
          }
        } catch (externalError) {
          if (recipeSearchRequestIdRef.current !== currentRequestId) return;
          console.error('Error expanding sparse recipes/ingredients search:', externalError);
        }
      } catch (error) {
        if (recipeSearchRequestIdRef.current !== currentRequestId) return;
        console.error('Error fetching cached recipes/ingredients:', error);
        setRecipeSearchResults([]);
        setShowRecipeResults(true);
        setHasAnyResults(false);
      } finally {
        if (recipeSearchRequestIdRef.current === currentRequestId) {
          setIsSearchingRecipes(false);
          setIsLoadingMore(false);
        }
      }
    };

    performSearch();
  }, [debouncedRecipeSearch, clientId, buildRecipeSearchParams]);

  const handleRecipeSearchSubmit = async () => {
    const searchQuery = recipeSearchTerm.trim();
    if (!clientId || searchQuery.length < 2) {
      return;
    }

    lastSubmittedRecipeSearchRef.current = searchQuery;
    const currentRequestId = recipeSearchRequestIdRef.current + 1;
    recipeSearchRequestIdRef.current = currentRequestId;
    const searchParams = buildRecipeSearchParams(searchQuery);

    setIsSearchingRecipes(true);
    setShowRecipeResults(true);
    setIsLoadingMore(false);

    try {
      const response = await searchMealPlanItems({
        ...searchParams,
        source: 'all',
      });
      if (recipeSearchRequestIdRef.current !== currentRequestId) return;

      const results = rankSearchResults(
        dedupeSearchResults(mapSearchResults(response?.results ?? [])),
        searchParams.q
      );
      setRecipeSearchResults(results);
      setHasAnyResults(results.length > 0);
    } catch (error) {
      if (recipeSearchRequestIdRef.current !== currentRequestId) return;
      console.error('Error fetching expanded recipes/ingredients:', error);
    } finally {
      if (recipeSearchRequestIdRef.current === currentRequestId) {
        setIsSearchingRecipes(false);
        setIsLoadingMore(false);
      }
    }
  };


  const handleRecipeSelect = async (recipe: RecipeSearchResult) => {
    const clinicalWarnings = Array.isArray((recipe as any).clinicalWarnings)
      ? (recipe as any).clinicalWarnings
      : [];
    const blockingIssue = clinicalWarnings.find((issue: any) => issue?.severity === 'blocking');
    if (blockingIssue?.message) {
      showErrorToast(String(blockingIssue.message), 'Clinically blocked');
      return;
    }
    const firstClinicalWarning = clinicalWarnings.find((issue: any) => issue?.severity === 'warning');
    if (firstClinicalWarning?.message) {
      showWarningToast(String(firstClinicalWarning.message), 'Clinical warning', { duration: 7000 });
    }
    // Set loading state for this recipe
    setAddingRecipeId(recipe.id);
    const recipeRecord = recipe as RecipeSearchResult & {
      _ingredient?: { defaultServingId?: string; servingId?: string };
      _rawSource?: unknown;
      _searchItemType?: unknown;
      _sourceId?: unknown;
      provider?: unknown;
    };
    const recipeResultId = String(recipe.id || '').trim();
    const rawSource = recipeRecord._rawSource ?? recipe.source;
    const provider = normalizeManualProvider(recipeRecord.provider, rawSource);
    const sourceForDetails = provider === 'internal' ? 'internal' : (provider === 'edamam' ? 'edamam' : 'spoonacular');
    const isIngredient = recipeRecord._searchItemType === 'ingredient';
    const selectedNutritionServings = isIngredient
      ? 1
      : (
        parseRecipeServingValue(recipeSearchSelectedServings[recipeResultId])
        ?? getRecipeDefaultServing(recipe)
      );
    const availableServingSizes = isIngredient ? [] : normalizeRecipeServingSizes(recipe);
    const defaultRecipeServingId = isIngredient ? null : getRecipeDefaultServingId(recipe);
    const selectedRecipeServingId = isIngredient
      ? null
      : (
        parseRecipeServingId(recipeSearchSelectedServingIds[recipeResultId])
        ?? defaultRecipeServingId
      );
    const defaultRecipeServing = defaultRecipeServingId
      ? availableServingSizes.find((entry) => entry.servingId === defaultRecipeServingId) ?? null
      : null;
    const selectedRecipeServing = selectedRecipeServingId
      ? availableServingSizes.find((entry) => entry.servingId === selectedRecipeServingId) ?? null
      : null;
    const defaultRecipeServingGrams = defaultRecipeServing?.grams ?? null;
    const selectedRecipeServingGrams = selectedRecipeServing?.grams ?? defaultRecipeServingGrams;
    const servingSizeMultiplier = (
      selectedRecipeServingGrams != null
      && defaultRecipeServingGrams != null
      && defaultRecipeServingGrams > 0
    )
      ? Math.round((selectedRecipeServingGrams / defaultRecipeServingGrams) * 10000) / 10000
      : 1;
    const ingredientData = recipeRecord._ingredient;
    const ingredientSourceId = String(recipeRecord._sourceId ?? '').trim();
    const ingredientUnit = ingredientData?.defaultServingId || ingredientData?.servingId || 'g';
    const ingredientName = (recipe.title || recipe.name || recipe.label || '').trim();
    const ingredientLookupId = isIngredient
      ? normalizeIngredientLookupId(String(recipe.id || ingredientSourceId || '').trim())
      : '';
    const ingredientNutritionSource: 'spoonacular' | 'edamam' | 'internal' =
      provider === 'internal' || provider === 'edamam' ? provider : 'spoonacular';
    let calories = Number((recipe.calories || 0).toFixed(2));
    let protein = Number((recipe.protein || 0).toFixed(2));
    let carbs = Number((recipe.carbs || 0).toFixed(2));
    let fat = Number((recipe.fat || 0).toFixed(2));

    // For ingredient selections, fetch nutrition from /ingredients/nutrition for accurate values.
    if (isIngredient && ingredientName) {
      try {
        const nutritionResponse = ingredientLookupId
          ? await getIngredientNutritionByIngredientId(
              ingredientLookupId,
              1,
              ingredientUnit
            )
          : await getIngredientNutrition(
              ingredientName,
              ingredientNutritionSource,
              1,
              ingredientUnit
            );
        calories = Number((nutritionResponse?.nutrition?.calories || 0).toFixed(2));
        protein = Number((nutritionResponse?.nutrition?.macros?.protein || 0).toFixed(2));
        carbs = Number((nutritionResponse?.nutrition?.macros?.carbs || 0).toFixed(2));
        fat = Number((nutritionResponse?.nutrition?.macros?.fat || 0).toFixed(2));
      } catch (error) {
        console.error('Error fetching ingredient nutrition values:', error);
      }
    }
    
    // Create FoodItem from recipe data for optimistic UI update
    const recipeAsFood: FoodItem = {
      id: recipe.id || uuidv4(),
      ...(isIngredient && ingredientLookupId ? { sourceId: ingredientLookupId } : {}),
      name: recipe.title || recipe.label || 'Unknown Recipe',
      calories,
      protein,
      carbs,
      fat,
      category: isIngredient ? 'ingredient' : 'recipe',
      healthLabels: recipe.healthLabels || normalizeStringList((recipe as any).dietLabels ?? (recipe as any).dietaryTagIds),
      allergens: normalizeStringList((recipe as any).allergens ?? (recipe as any).allergenTagIds),
      intoleranceTags: normalizeStringList((recipe as any).intoleranceTags ?? (recipe as any).intoleranceTagIds),
      serving: isIngredient ? `1 ${ingredientUnit}` : '', // Serving text not displayed in UI
      source: sourceForDetails,
      provider,
      nutritionServings: isIngredient ? 1 : selectedNutritionServings,
      ...(!isIngredient ? {
        defaultServingId: defaultRecipeServingId,
        defaultServingGrams: defaultRecipeServingGrams,
        selectedServingId: selectedRecipeServingId,
        selectedServingGrams: selectedRecipeServingGrams,
        servingSizeMultiplier,
        servingSizes: availableServingSizes,
      } : {}),
      ...(isIngredient ? { amount: 1, servingId: ingredientUnit } : {}),
    };
    
    // Immediately add to UI (optimistic update)
    onAddFood(recipeAsFood, selectedDay, slot.type, slot.stateKey);
    const currentIndex = slot.items.length; // The index where this recipe will be added
    
    // Set loading state for this recipe
    setRecipeStates(prev => ({ ...prev, [currentIndex]: 'loading' }));
    
    // Close dropdown and clear search term after selection
    setShowRecipeResults(false);
    // Clear the search field after selection
    setRecipeSearchTerm('');
    // Clear the just-selected ref since we're clearing the search
    justSelectedRecipeRef.current = null;

    // Validate that we have a proper recipe/ingredient ID
    if (!recipe.id) {
      console.error('Missing recipe/ingredient ID:', recipe);
      showErrorToast("Missing recipe ID. Please try selecting the recipe again.", "Error");
      setRecipeStates(prev => ({ ...prev, [currentIndex]: 'error' }));
      setAddingRecipeId(null);
      return;
    }
    
    
    // If draftId is available, make API call in background
    if (draftId) {
      try {
        if (isIngredient) {
          const ingredientProvider: 'spoonacular' | 'internal' =
            provider === 'internal' ? 'internal' : 'spoonacular';
          const addItemPayload = {
            draftId,
            day: selectedDay,
            mealName: slot.name,
            itemType: 'ingredient' as const,
            ingredient: {
              id: ingredientLookupId || ingredientSourceId || recipe.id,
              provider: ingredientProvider,
              amount: 1,
              servingId: ingredientData?.defaultServingId || 'g',
            },
          };
          const apiResponse = await addManualMealPlanItem(addItemPayload);
          if (apiResponse.success) {
            setRecipeStates(prev => ({ ...prev, [currentIndex]: 'success' }));
            if (apiResponse.data && onDraftDataUpdate) {
              onDraftDataUpdate(apiResponse.data);
            }
          } else {
            throw new Error(apiResponse.message || 'Failed to add ingredient');
          }
        } else {
          // POST /meal-plans/manual/add-item with itemType: "recipe"
          const addItemPayload = {
            draftId,
            day: selectedDay,
            mealName: slot.name,
            itemType: 'recipe' as const,
            recipe: {
              id: recipe.id,
              provider,
              source: provider === 'internal' ? ('cached' as const) : ('api' as const),
            },
            nutritionServings: selectedNutritionServings,
            selectedServingId: selectedRecipeServingId ?? undefined,
          };
          const apiResponse = await addManualMealPlanItem(addItemPayload);
          if (apiResponse.success) {
            setRecipeStates(prev => ({ ...prev, [currentIndex]: 'success' }));
            if (apiResponse.data && onDraftDataUpdate) {
              onDraftDataUpdate(apiResponse.data);
            }
          } else {
            throw new Error(apiResponse.message || 'Failed to add recipe to meal plan');
          }
        }
      } catch (error) {
        console.error('Error adding recipe/ingredient:', error);
        setRecipeStates(prev => ({ ...prev, [currentIndex]: 'error' }));
        showErrorToast("Item added to plan but not saved. Click retry to save.", "Error Saving");
      } finally {
        setAddingRecipeId(null);
      }
    } else {
      setRecipeStates(prev => ({ ...prev, [currentIndex]: 'success' }));
      setAddingRecipeId(null);
    }
  };

  
  const handleRetryRecipe = async (food: FoodItem, index: number) => {
    if (!draftId) return;
    
    setRecipeStates(prev => ({ ...prev, [index]: 'loading' }));
    
    try {
      const retryProvider = normalizeManualProvider(food.provider, food.source);
      const retryNutritionServings = parseRecipeServingValue(food.nutritionServings) ?? 1;
      // POST /meal-plans/manual/add-item (retry uses recipe payload)
      const addItemPayload = {
        draftId,
        day: selectedDay,
        mealName: slot.name,
        itemType: 'recipe' as const,
        recipe: {
          id: food.id,
          provider: retryProvider,
          source: retryProvider === 'internal' ? ('cached' as const) : ('api' as const),
        },
        nutritionServings: retryNutritionServings,
        selectedServingId: parseRecipeServingId(food.selectedServingId) ?? undefined,
      };
      const apiResponse = await addManualMealPlanItem(addItemPayload);
      if (apiResponse.success) {
        setRecipeStates(prev => ({ ...prev, [index]: 'success' }));
        if (apiResponse.data && onDraftDataUpdate) {
          onDraftDataUpdate(apiResponse.data);
        }
      } else {
        throw new Error(apiResponse.message || 'Failed to save recipe');
      }
    } catch (error) {
      console.error('Error retrying recipe save:', error);
      setRecipeStates(prev => ({ ...prev, [index]: 'error' }));
      showErrorToast("Failed to save recipe. Please try again.", "Save Failed");
    }
  };

  const handleAddIngredientFromModal = async (
    ingredient: { id: string; name: string; units: string[]; provider: 'spoonacular' | 'internal' },
    quantity: number,
    unit: string
  ) => {
    if (!draftId) return;

    const sourceForDetails = ingredient.provider === 'internal' ? 'internal' : 'spoonacular';
    const currentIndex = slot.items.length;
    setAddingRecipeId(ingredient.id);

    const ingredientAsFood: FoodItem = {
      id: ingredient.id || uuidv4(),
      name: ingredient.name,
      calories: 0,
      protein: 0,
      carbs: 0,
      fat: 0,
      category: 'ingredient',
      serving: `${quantity} ${unit}`,
      source: sourceForDetails,
      provider: ingredient.provider,
      nutritionServings: quantity,
      amount: quantity,
      servingId: unit,
    };

    onAddFood(ingredientAsFood, selectedDay, slot.type, slot.stateKey);
    setRecipeStates((prev) => ({ ...prev, [currentIndex]: 'loading' }));

    try {
      const apiResponse = await addManualMealPlanItem({
        draftId,
        day: selectedDay,
        mealName: slot.name,
        itemType: 'ingredient',
        ingredient: {
          id: ingredient.id,
          provider: ingredient.provider,
          amount: quantity,
          servingId: unit,
        },
      });

      if (!apiResponse.success) {
        throw new Error(apiResponse.message || 'Failed to add ingredient');
      }

      setRecipeStates((prev) => ({ ...prev, [currentIndex]: 'success' }));
      if (apiResponse.data && onDraftDataUpdate) {
        onDraftDataUpdate(apiResponse.data);
      }
    } catch (error) {
      console.error('Error adding ingredient:', error);
      setRecipeStates((prev) => ({ ...prev, [currentIndex]: 'error' }));
      showErrorToast("Ingredient added to plan but not saved. Click retry to save.", "Error Saving");
    } finally {
      setAddingRecipeId(null);
    }
  };

  const handleReplaceIngredientNoop = () => undefined;

  const handleSave = async () => {
    setIsUpdatingSlot(true);
    try {
      // If draftId exists, make API call to update meal slot
      if (draftId) {
        const response = await updateMealSlot(
          draftId,
          selectedDay,
          slot.name,
          editTimeSlot,
          editTargetKcal,
          {
            mealType: slot.type,
            currentMealName: slot.name,
            currentMealTime: slot.timeSlot,
            newMealName: editName,
          },
        );

        if (response.success) {
        

          // Use response data to update draft if available
          if (response.data && onDraftDataUpdate) {
            onDraftDataUpdate(response.data);
          }
        }
      }

      // Update local state
    if (onUpdateMealInfo) {
      onUpdateMealInfo(selectedDay, slot.type, {
        name: editName,
        timeSlot: editTimeSlot,
        targetKcal: editTargetKcal
      }, slot.stateKey);
    }
    setIsEditing(false);
    } catch (error) {
      console.error('Error updating meal slot:', error);
      showErrorToast(error, "Error");
    } finally {
      setIsUpdatingSlot(false);
    }
  };

  const handleCancel = () => {
    setEditName(slot.name);
    setEditTimeSlot(slot.timeSlot);
    setEditTargetKcal(slot.targetKcal);
    setIsEditing(false);
  };

  const [isRecipeModalOpen, setIsRecipeModalOpen] = useState(false);
  const [isDeleteMealSlotConfirmOpen, setIsDeleteMealSlotConfirmOpen] = useState(false);
  const handleDeleteMealSlotConfirm = async () => {
    setIsDeleteMealSlotConfirmOpen(false);
    if (!onDeleteMealSlot || isDeletingAnyMealSlot) return;
    const slotMealOrder = Number(mealOrder);
    const mealTime = String(slot.timeSlot ?? '').trim();
    const mealType = String(slot.mealType ?? slot.type ?? '').trim();
    const deleteOptions: DeleteMealSlotOptions = {
      ...(Number.isFinite(slotMealOrder) && slotMealOrder > 0 ? { mealOrder: Math.floor(slotMealOrder) } : {}),
      ...(mealType ? { mealType } : {}),
      ...(mealTime ? { mealTime, currentMealTime: mealTime } : {}),
    };
    setIsDeletingSlot(true);
    try {
      await onDeleteMealSlot(selectedDay, slot.name, deleteOptions);
    } catch (error) {
      console.error('Error deleting meal slot:', error);
      showErrorToast(error, 'Error');
    } finally {
      setIsDeletingSlot(false);
    }
  };

  const [selectedRecipe, setSelectedRecipe] = useState<{ 
    id: string; 
    name: string;
    source?: 'spoonacular' | 'edamam' | 'internal';
  } | null>(null);
  const [isRefreshingDraft, setIsRefreshingDraft] = useState(false);
  
  // Track loading/error state for each recipe by index
  const [recipeStates, setRecipeStates] = useState<{
    [index: number]: 'loading' | 'success' | 'error';
  }>({});
  
  // Track which recipe is being deleted
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);
  
  // Track if any deletion is in progress globally
  const [isDeletingAny, setIsDeletingAny] = useState(false);

  // Track servings for each food item
  const [foodServings, setFoodServings] = useState<{
    [index: number]: number;
  }>({});
  const [foodServingsInput, setFoodServingsInput] = useState<{
    [index: number]: string;
  }>({});
  const [foodServingSizeIds, setFoodServingSizeIds] = useState<{
    [index: number]: string;
  }>({});

  // Track if servings is being updated for any food item
  const [updatingServingsIndex, setUpdatingServingsIndex] = useState<number | null>(null);
  const [updatingIngredientUnitIndex, setUpdatingIngredientUnitIndex] = useState<number | null>(null);
  const [loadingIngredientUnitsIndex, setLoadingIngredientUnitsIndex] = useState<number | null>(null);

  // Track units and selected unit per ingredient item index
  const [ingredientUnits, setIngredientUnits] = useState<{ [index: number]: string[] }>({});
  /** When set, unit dropdown shows labels like "serving (56g)" from ingredients/details API */
  const [ingredientUnitOptions, setIngredientUnitOptions] = useState<{ [index: number]: Array<{ value: string; label: string }> }>({});
  const [ingredientUnitOverrides, setIngredientUnitOverrides] = useState<{ [index: number]: string }>({});

  // Debounce timer for servings updates
  const servingsDebounceTimersRef = useRef<{ [index: number]: NodeJS.Timeout }>({});

  // Initialize servings from slot items (only used when draftId exists for edit mode)
  useEffect(() => {
    if (!draftId) return; // Only initialize if in edit mode
    
    const initialServings: { [index: number]: number } = {};
    slot.items.forEach((food, index) => {
      if (food.category === 'ingredient') {
        initialServings[index] = food.amount ?? food.nutritionServings ?? 1;
      } else {
        initialServings[index] = food.nutritionServings || food.servings || 1;
      }
    });
    setFoodServings(initialServings);
    setFoodServingsInput({});
  }, [slot.items, draftId]);

  useEffect(() => {
    const initialServingSizeIds: { [index: number]: string } = {};
    slot.items.forEach((food, index) => {
      if (food.category !== 'recipe') return;
      const selectedServingId =
        parseRecipeServingId(food.selectedServingId)
        ?? parseRecipeServingId(food.defaultServingId);
      if (selectedServingId) {
        initialServingSizeIds[index] = selectedServingId;
      }
    });
    setFoodServingSizeIds(initialServingSizeIds);
  }, [slot.items]);

  useEffect(() => {
    const initialUnits: { [index: number]: string } = {};
    slot.items.forEach((food, index) => {
      if (food.category === 'ingredient') {
        initialUnits[index] = food.servingId ?? 'g';
      }
    });
    setIngredientUnitOverrides(initialUnits);
  }, [slot.items]);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      Object.values(servingsDebounceTimersRef.current).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);

  // Load ingredient unit options on mount so default serving shows with grams (e.g. "serving (56g)")
  useEffect(() => {
    slot.items.forEach((food, index) => {
      if (food.category === 'ingredient') {
        loadIngredientUnits(food, index);
      }
    });
  }, [slot.items]);

  const refreshDraftDataFromApi = async () => {
    if (!draftId || !onDraftDataUpdate) return;
    try {
      const draftResponse: any = await getManualMealPlanDraft(draftId);
      if (draftResponse?.data) {
        onDraftDataUpdate(draftResponse.data);
      }
    } catch (error) {
      console.error('Error refreshing draft data:', error);
    }
  };

  const loadIngredientUnits = async (food: FoodItem, index: number) => {
    if (food.category !== 'ingredient') return;
    if (ingredientUnits[index] && ingredientUnits[index].length > 0) return;

    const currentAmount = foodServings[index] ?? food.amount ?? 1;
    const currentUnit = ingredientUnitOverrides[index] ?? food.servingId ?? 'g';
    const recipeIngredientId = resolveRecipeIngredientId(food);
    const ingredientLookupId = resolveIngredientLookupId(food);

    setLoadingIngredientUnitsIndex(index);
    try {
      let units: string[] = [];
      let unitOptions: Array<{ value: string; label: string }> | undefined;

      if (recipeIngredientId) {
        try {
          const nutritionResponse = await getIngredientNutritionByRecipeIngredientId(
            recipeIngredientId,
            currentAmount,
            currentUnit || undefined,
          );
          const metadata = extractIngredientUnitMetadata(nutritionResponse);
          if (metadata.units.length > 0) {
            units = metadata.units;
            unitOptions = buildIngredientUnitOptions(metadata.units, metadata.labelsByKey, currentUnit);
          }
        } catch (error) {
          console.warn('Recipe ingredient nutrition lookup failed, falling back to ingredient details:', error);
        }
      }

      if (!units.length && ingredientLookupId) {
        const detailsResponse = await getIngredientDetails(ingredientLookupId, currentAmount, currentUnit);
        units = Array.isArray(detailsResponse.units) ? detailsResponse.units : [];
        unitOptions = detailsResponse.unitOptions;
      }

      if (!units.length) {
        const fallbackSource: 'all' | 'spoonacular' | 'internal' =
          food.source === 'internal' ? 'internal' : food.source === 'spoonacular' ? 'spoonacular' : 'all';
        const fallback = await getIngredientsAutocomplete(
          food.name,
          'units_only',
          currentUnit,
          fallbackSource,
          allergies,
          clientId
        );
        units = Array.isArray(fallback.units) ? fallback.units : [];
        unitOptions = undefined;
      }

      const normalized = [...new Set([currentUnit, ...units].filter(Boolean).map((u) => String(u).trim()))];
      setIngredientUnits((prev) => ({ ...prev, [index]: normalized }));
      setIngredientUnitOptions((prev) => ({
        ...prev,
        [index]:
          unitOptions && unitOptions.length > 0
            ? buildIngredientUnitOptions(
                normalized,
                Object.fromEntries(
                  unitOptions
                    .map((option) => [normalizeIngredientUnitKey(option.value), option.label] as const)
                    .filter(([value, label]) => value.length > 0 && label.length > 0),
                ),
                currentUnit,
              )
            : [],
      }));
    } catch (error) {
      console.error('Error loading ingredient units:', error);
      const fallbackUnits = [currentUnit].filter(Boolean);
      setIngredientUnits((prev) => ({ ...prev, [index]: fallbackUnits }));
      setIngredientUnitOptions((prev) => ({ ...prev, [index]: [] }));
    } finally {
      setLoadingIngredientUnitsIndex(null);
    }
  };

  const updateServingsWithDebounce = (foodIndex: number, newServings: number) => {
    // Update UI immediately for responsive feel
    setFoodServings(prev => ({
      ...prev,
      [foodIndex]: newServings
    }));
    
    // Clear any existing timer for this food item
    if (servingsDebounceTimersRef.current[foodIndex]) {
      clearTimeout(servingsDebounceTimersRef.current[foodIndex]);
    }
    
    // Set new timer for API call (800ms debounce)
    servingsDebounceTimersRef.current[foodIndex] = setTimeout(() => {
      handleUpdateServings(foodIndex, newServings);
    }, 800);
  };

  const handleUpdateServings = async (foodIndex: number, newServings: number) => {
    const food = slot.items[foodIndex];
    if (!food || !draftId) return;

    try {
      setUpdatingServingsIndex(foodIndex);

      const response =
        food.category === 'ingredient'
          ? await updateManualMealPlanIngredient({
              draftId,
              day: selectedDay,
              mealName: slot.name,
              ingredientId: food.id,
              amount: newServings,
              servingId: ingredientUnitOverrides[foodIndex] ?? food.servingId ?? 'g',
            })
          : await updateManualMealPlanServings({
              draftId,
              day: selectedDay,
              mealName: slot.name,
              itemId: food.id,
              nutritionServings: newServings,
              servings: newServings,
              selectedServingId:
                parseRecipeServingId(foodServingSizeIds[foodIndex])
                ?? parseRecipeServingId(food.selectedServingId)
                ?? undefined,
            });

      if (response.success) {
        // foodServings already updated in debounce function
        await refreshDraftDataFromApi();
      } else {
        // Revert to original value on failure
        const food = slot.items[foodIndex];
        const originalServings = food.category === 'ingredient'
          ? (food.amount ?? food.nutritionServings ?? 1)
          : (food.nutritionServings || food.servings || 1);
        setFoodServings(prev => ({
          ...prev,
          [foodIndex]: originalServings
        }));
      }
    } catch (error: any) {
      console.error('Error updating servings:', error);

      // Revert to original value on error
      const food = slot.items[foodIndex];
      const originalServings = food?.category === 'ingredient'
        ? (food.amount ?? food.nutritionServings ?? 1)
        : (food?.nutritionServings || food?.servings || 1);
      setFoodServings(prev => ({
        ...prev,
        [foodIndex]: originalServings
      }));
      
      showErrorToast(error, "Update Failed");
    } finally {
      setUpdatingServingsIndex(null);
    }
  };

  const handleRecipeServingSizeChange = async (foodIndex: number, servingId: string) => {
    const food = slot.items[foodIndex];
    if (!food || food.category !== 'recipe' || !draftId) return;

    const normalizedServingId = parseRecipeServingId(servingId);
    if (!normalizedServingId) return;

    const availableServingIds = (food.servingSizes ?? [])
      .map((entry) => parseRecipeServingId(entry.servingId))
      .filter((entry): entry is string => entry !== null);
    if (availableServingIds.length > 0 && !availableServingIds.includes(normalizedServingId)) {
      return;
    }

    setFoodServingSizeIds((prev) => ({ ...prev, [foodIndex]: normalizedServingId }));
    setUpdatingServingsIndex(foodIndex);
    try {
      const currentServings = foodServings[foodIndex] ?? food.nutritionServings ?? food.servings ?? 1;
      const response = await updateManualMealPlanServings({
        draftId,
        day: selectedDay,
        mealName: slot.name,
        itemId: food.id,
        nutritionServings: currentServings,
        servings: currentServings,
        selectedServingId: normalizedServingId,
      });

      if (response.success) {
        await refreshDraftDataFromApi();
      } else {
        throw new Error(response.message || 'Failed to update serving size');
      }
    } catch (error: any) {
      console.error('Error updating serving size:', error);
      setFoodServingSizeIds((prev) => {
        const next = { ...prev };
        const fallbackServingId =
          parseRecipeServingId(food.selectedServingId)
          ?? parseRecipeServingId(food.defaultServingId)
          ?? parseRecipeServingId(food.servingSizes?.find((entry) => entry.isDefault)?.servingId)
          ?? parseRecipeServingId(food.servingSizes?.[0]?.servingId);
        if (fallbackServingId) {
          next[foodIndex] = fallbackServingId;
        } else {
          delete next[foodIndex];
        }
        return next;
      });
      showErrorToast(error, "Update Failed");
    } finally {
      setUpdatingServingsIndex(null);
    }
  };

  const handleIngredientUnitChange = (foodIndex: number, newUnit: string) => {
    const food = slot.items[foodIndex];
    if (!food || food.category !== 'ingredient') return;
    setIngredientUnitOverrides((prev) => ({ ...prev, [foodIndex]: newUnit }));
  };

  const isFoodAmountBusy = (foodIndex: number) => {
    const food = slot.items[foodIndex];
    if (!food) return false;
    return food.category === 'ingredient'
      ? updatingIngredientUnitIndex === foodIndex
      : updatingServingsIndex === foodIndex;
  };

  const commitTypedAmount = (foodIndex: number) => {
    const food = slot.items[foodIndex];
    if (!food || isFoodAmountBusy(foodIndex)) return;

    const rawInput = foodServingsInput[foodIndex];
    if (rawInput == null) return;

    const parsed = Number.parseFloat(rawInput);
    if (!Number.isFinite(parsed)) {
      setFoodServingsInput((prev) => {
        const next = { ...prev };
        delete next[foodIndex];
        return next;
      });
      return;
    }

    const rounded = Math.round(parsed * 10) / 10;
    const clamped = food.category === 'ingredient'
      ? Math.max(0.1, rounded)
      : Math.min(20, Math.max(0.1, rounded));

    setFoodServings((prev) => ({ ...prev, [foodIndex]: clamped }));
    setFoodServingsInput((prev) => {
      const next = { ...prev };
      delete next[foodIndex];
      return next;
    });

    if (food.category !== 'ingredient') {
      updateServingsWithDebounce(foodIndex, clamped);
    }
  };

  const handleTypedAmountChange = (foodIndex: number, rawValue: string) => {
    if (isFoodAmountBusy(foodIndex)) return;
    if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
      setFoodServingsInput((prev) => ({ ...prev, [foodIndex]: rawValue }));
    }
  };

  const handleIncreaseServings = (foodIndex: number) => {
    if (isFoodAmountBusy(foodIndex)) return;
    const food = slot.items[foodIndex];
    const currentServings = foodServings[foodIndex] || 1;
    const increased = Math.round((currentServings + 0.5) * 10) / 10;
    const newServings = food?.category === 'ingredient' ? increased : Math.min(20, increased);
    setFoodServingsInput((prev) => {
      const next = { ...prev };
      delete next[foodIndex];
      return next;
    });
    if (food?.category === 'ingredient') {
      setFoodServings(prev => ({ ...prev, [foodIndex]: newServings }));
      return;
    }
    updateServingsWithDebounce(foodIndex, newServings);
  };

  const handleDecreaseServings = (foodIndex: number) => {
    if (isFoodAmountBusy(foodIndex)) return;
    const food = slot.items[foodIndex];
    const currentServings = foodServings[foodIndex] || 1;
    const newServings = Math.max(0.1, Math.round((currentServings - 0.5) * 10) / 10);
    setFoodServingsInput((prev) => {
      const next = { ...prev };
      delete next[foodIndex];
      return next;
    });
    if (food?.category === 'ingredient') {
      setFoodServings(prev => ({ ...prev, [foodIndex]: newServings }));
      return;
    }
    updateServingsWithDebounce(foodIndex, newServings);
  };

  const handleSaveIngredientChanges = async (foodIndex: number) => {
    const food = slot.items[foodIndex];
    if (!food || food.category !== 'ingredient' || !draftId) return;

    const amount = foodServings[foodIndex] ?? food.amount ?? food.nutritionServings ?? 1;
    const servingId = ingredientUnitOverrides[foodIndex] ?? food.servingId ?? 'g';
    const previousAmount = food.amount ?? food.nutritionServings ?? 1;
    const previousUnit = food.servingId ?? 'g';

    setUpdatingIngredientUnitIndex(foodIndex);
    try {
      const response = await updateManualMealPlanIngredient({
        draftId,
        day: selectedDay,
        mealName: slot.name,
        ingredientId: food.id,
        amount,
        servingId,
      });

      if (response.success) {
        await refreshDraftDataFromApi();
      } else {
        setFoodServings((prev) => ({ ...prev, [foodIndex]: previousAmount }));
        setIngredientUnitOverrides((prev) => ({ ...prev, [foodIndex]: previousUnit }));
      }
    } catch (error: any) {
      console.error('Error updating ingredient:', error);
      setFoodServings((prev) => ({ ...prev, [foodIndex]: previousAmount }));
      setIngredientUnitOverrides((prev) => ({ ...prev, [foodIndex]: previousUnit }));
      showErrorToast(error, "Update Failed");
    } finally {
      setUpdatingIngredientUnitIndex(null);
    }
  };

  const handleEditFood = (food: FoodItem, index: number) => {
    setSelectedRecipe({ 
      id: food.id, 
      name: food.name,
      source: food.source || 'spoonacular' // Use stored source or default to spoonacular
    });
    setIsRecipeModalOpen(true);
  };

  const handleCloseRecipeModal = async () => {
    setIsRecipeModalOpen(false);
    setSelectedRecipe(null);
    
    // Refresh draft data to get updated servings
    if (draftId && onDraftDataUpdate) {
      setIsRefreshingDraft(true);
      try {
        await refreshDraftDataFromApi();
      } catch (error) {
        console.error('Error refreshing draft data:', error);
        // Don't show error toast - this is a background refresh
      } finally {
        setIsRefreshingDraft(false);
      }
    }
  };

  const getFoodBaseAmount = (food: FoodItem): number => {
    const rawBaseAmount =
      food.category === 'ingredient'
        ? (food.amount ?? food.nutritionServings ?? 1)
        : (food.nutritionServings || food.servings || 1);
    return Number.isFinite(rawBaseAmount) && rawBaseAmount > 0 ? rawBaseAmount : 1;
  };

  const getFoodCurrentAmount = (food: FoodItem, index: number): number => {
    const currentFromState = foodServings[index];
    if (Number.isFinite(currentFromState) && currentFromState > 0) {
      return currentFromState;
    }
    return getFoodBaseAmount(food);
  };

  const canScaleFoodNutrition = (food: FoodItem, index: number): boolean => {
    if (food.category !== 'ingredient') return true;
    const baseUnit = food.servingId ?? 'g';
    const selectedUnit = ingredientUnitOverrides[index] ?? baseUnit;
    return selectedUnit === baseUnit;
  };

  const getDisplayNutrition = (food: FoodItem, index: number) => {
    const scaleFactor =
      canScaleFoodNutrition(food, index)
        ? getFoodCurrentAmount(food, index) / getFoodBaseAmount(food)
        : 1;

    return {
      calories: toFiniteNumber(food.calories) * scaleFactor,
      protein: toFiniteNumber(food.protein) * scaleFactor,
      fat: toFiniteNumber(food.fat) * scaleFactor,
      carbs: toFiniteNumber(food.carbs) * scaleFactor,
    };
  };

  const totalKcal = slot.items.reduce((sum, item, index) => {
    const displayNutrition = getDisplayNutrition(item, index);
    return sum + displayNutrition.calories;
  }, 0);
  const totalCarbs = slot.items.reduce((sum, item, index) => {
    const displayNutrition = getDisplayNutrition(item, index);
    return sum + displayNutrition.carbs;
  }, 0);
  const kcalProgress = slot.targetKcal
    ? Math.min(100, Math.max(0, (totalKcal / slot.targetKcal) * 100))
    : 0;
  const isOverTarget = slot.targetKcal ? totalKcal > slot.targetKcal : false;
  const kcalDifference = slot.targetKcal ? totalKcal - slot.targetKcal : 0;

  // Hide card with fade out effect when deleting
  if (isDeletingSlot) {
  return (
      <Card className="border-l-4 opacity-50 animate-pulse" style={{ borderLeftColor: getMealColor(slot.type) }}>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg">{slot.name}</CardTitle>
            <Loader2 className="h-5 w-5 animate-spin text-red-500" />
          </div>
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            Deleting meal slot...
          </div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="border-l-4 transition-all relative" style={{ borderLeftColor: getMealColor(slot.type) }}>
      {/* Refreshing Draft Overlay */}
      {isRefreshingDraft && (
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            <span className="text-sm font-medium text-gray-700">Updating servings...</span>
          </div>
        </div>
      )}
      
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            {isEditing ? (
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label htmlFor={`meal-name-${slot.id}`} className="text-xs font-medium text-muted-foreground">
                      Meal Name
                    </Label>
                    <Input
                      id={`meal-name-${slot.id}`}
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="h-8 text-sm"
                      placeholder="Enter meal name"
                      disabled={isUpdatingSlot}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`meal-time-${slot.id}`} className="text-xs font-medium text-muted-foreground">
                      Meal Time
                    </Label>
                    <Input
                      id={`meal-time-${slot.id}`}
                      type="time"
                      value={editTimeSlot}
                      onChange={(e) => setEditTimeSlot(e.target.value)}
                      className="h-8 text-sm"
                      disabled={isUpdatingSlot}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`meal-kcal-${slot.id}`} className="text-xs font-medium text-muted-foreground">
                      Target Kcal (Optional)
                    </Label>
                    <Input
                      id={`meal-kcal-${slot.id}`}
                      type="number"
                      min="0"
                      step="1"
                      value={editTargetKcal !== null && editTargetKcal !== undefined ? editTargetKcal : ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          setEditTargetKcal(null);
                        } else {
                          const numValue = parseInt(value);
                          if (!isNaN(numValue) && numValue >= 0) {
                            setEditTargetKcal(numValue);
                          }
                        }
                      }}
                      onBlur={(e) => {
                        // Ensure we have a valid number on blur, or allow empty (null)
                        const value = e.target.value;
                        if (value !== '' && parseInt(value) <= 0) {
                          setEditTargetKcal(slot.targetKcal ?? null); // Reset to original value (can be null)
                        }
                      }}
                      className="h-8 text-sm"
                      placeholder="Target calories (optional)"
                      disabled={isUpdatingSlot}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSave} className="h-7" disabled={isUpdatingSlot}>
                    {isUpdatingSlot ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                    <Save className="h-3 w-3 mr-1" />
                    Save
                      </>
                    )}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancel} className="h-7" disabled={isUpdatingSlot}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{slot.name}</CardTitle>
                  <div className="flex gap-1">
                  {onUpdateMealInfo && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsEditing(true)}
                      className="h-6 w-6 p-0"
                      title="Edit meal info"
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                  )}
                    {onDeleteMealSlot && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsDeleteMealSlotConfirmOpen(true)}
                        className="h-6 w-6 p-0 hover:bg-red-50 hover:text-red-600"
                        title={isDeletingAnyMealSlot ? "Please wait for current meal slot deletion to complete" : "Delete meal slot"}
                        disabled={isDeletingAnyMealSlot}
                      >
                        {isDeletingSlot ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Trash2 className="h-3 w-3" />
                        )}
                    </Button>
                  )}
                </div>
                </div>
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  {slot.timeSlot && (
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                      <span>{formatTimeTo12Hour(slot.timeSlot)}</span>
                  </div>
                  )}
                  {slot.targetKcal && (
                  <div className="flex items-center gap-1">
                    <Target className="h-3 w-3" />
                      <span>{slot.targetKcal} kcal</span>
                  </div>
                  )}
                  <div className="flex items-center gap-1 font-medium text-sky-700" title="Total carbohydrate for this composed meal">
                    <span>Carbs: {formatNumber(totalCarbs)}g</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Kcal Progress Bar */}
        {!isEditing && slot.targetKcal && (
          <div className="mb-3">
            <div className="flex justify-between items-center text-xs text-muted-foreground mb-1">
              <span>Progress: {formatKcalValue(totalKcal)} / {formatKcalValue(slot.targetKcal)} kcal</span>
              <span className={isOverTarget ? 'text-red-500' : ''}>
                {isOverTarget ? '+' : ''}{formatKcalValue(Math.abs(kcalDifference))} kcal {isOverTarget ? 'over' : 'remaining'}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${
                  isOverTarget ? 'bg-red-500' : kcalProgress > 80 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(kcalProgress, 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Recipe Search */}
        {!isEditing && clientId && (
          <div className="mb-3 recipe-search-container" data-slot-id={slot.id}>
            <div className="mb-2 flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1"
                onClick={() => setIsIngredientModalOpen(true)}
                disabled={!draftId}
              >
                <Plus className="h-3.5 w-3.5" />
                Add Ingredient
              </Button>
            </div>
            <SearchableDropdown
                value={recipeSearchTerm}
              onChange={setRecipeSearchTerm}
              onSelect={handleRecipeSelect}
              resultsPosition="static"
              searchResults={recipeSearchResults}
              minSearchLength={2}
              isSearching={isSearchingRecipes}
              isLoadingMore={isLoadingMore}
              showResults={showRecipeResults}
              onClose={() => setShowRecipeResults(false)}
              onSearchSubmit={handleRecipeSearchSubmit}
              placeholder="Search food or ingredient"
              addingItemId={addingRecipeId}
              getItemId={(recipe) => recipe.id}
              getItemDisplayName={(recipe) => recipe.title || recipe.name}
              onFocus={() => {
                // Close other search dropdowns by dispatching a custom event
                window.dispatchEvent(new CustomEvent('closeOtherSearches', { detail: { currentSlotId: slot.id } }));
              }}
              renderEmptyState={() => (
                <div className="flex flex-col items-center gap-2 p-3 text-center">
                  <p className="text-xs text-gray-500">No results found</p>
                  {clientId && draftId ? (
                    <>
                      <p className="text-xs text-gray-400">
                        Create a custom recipe to add it to this meal.
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 border-dashed border-green-300 text-green-700 hover:bg-green-50 hover:border-green-400"
                        onMouseDown={(event) => {
                          // Prevent SearchableDropdown from closing before navigation.
                          event.preventDefault();
                        }}
                        onClick={() => {
                          const params = new URLSearchParams({
                            clientId,
                            draftId,
                            day: selectedDay.toString(),
                            mealName: slot.name,
                            from: `${location.pathname}${location.search}`,
                          });
                          navigate(`/recipes/create?${params.toString()}`);
                        }}
                      >
                        <ChefHat className="h-3.5 w-3.5 mr-1.5" />
                        Create Custom Recipe
                      </Button>
                    </>
                  ) : null}
                </div>
              )}
              renderResult={(recipe, index, isSelected) => {
                const isAdding = addingRecipeId === recipe.id;
                const domItemId = String((recipe as any).id ?? '').trim();
                const domItemType = String((recipe as any).type ?? (recipe as any)._searchItemType ?? '').trim();
                const domItemSource = String((recipe as any).source ?? (recipe as any)._rawSource ?? '').trim();
                const domItemProvider = String((recipe as any).provider ?? '').trim();
                const domItemSourceId = String((recipe as any).sourceId ?? (recipe as any)._sourceId ?? '').trim();
                const domItemInternalId = String((recipe as any).internalId ?? (recipe as any).internalIngredientId ?? '').trim();
                const domItemSpoonacularId = String((recipe as any).spoonacularId ?? (recipe as any).spoonacularIngredientId ?? '').trim();
                const domRawIsCached = (recipe as any).isCached;
                const domItemIsCached = typeof domRawIsCached === 'boolean'
                  ? domRawIsCached
                  : domItemSource.toLowerCase() === 'cached';
                const isRecipeResult = (recipe as any)?._searchItemType === 'recipe';
                const allowedServings = isRecipeResult ? getRecipeAllowedServings(recipe) : [];
                const availableServingSizes = isRecipeResult ? normalizeRecipeServingSizes(recipe) : [];
                const defaultServingSizeId = isRecipeResult ? getRecipeDefaultServingId(recipe) : null;
                const selectedServingSizeId = isRecipeResult
                  ? (
                    parseRecipeServingId(recipeSearchSelectedServingIds[String(recipe.id || '').trim()])
                    ?? defaultServingSizeId
                  )
                  : null;
                const selectedServingSize = selectedServingSizeId
                  ? availableServingSizes.find((entry) => entry.servingId === selectedServingSizeId)
                  : null;
                const rawSource = String((recipe as any)?._rawSource ?? '').trim().toLowerCase();
                const totalYieldServings = Number((recipe as any)?.servings);
                const showCustomYieldNote = isRecipeResult
                  && (rawSource === 'custom' || rawSource === 'manual' || (recipe as any)?.provider === 'internal')
                  && Number.isFinite(totalYieldServings)
                  && totalYieldServings > 1
                  && !!selectedServingSize;
                const perPersonShare = formatPerPersonShare(totalYieldServings);
                const selectedServingSizeLabel = formatUnitForDisplay(selectedServingSize?.label || selectedServingSize?.servingId || 'serving size');
                const selectedServing = isRecipeResult
                  ? (
                    parseRecipeServingValue(recipeSearchSelectedServings[String(recipe.id || '').trim()])
                    ?? getRecipeDefaultServing(recipe)
                  )
                  : 1;
                return (
                  <li
                    key={`recipe-${recipe.id || `index-${index}`}-${index}`}
                    className={`p-2 transition-all ${
                      isAdding 
                        ? 'bg-gradient-to-r from-blue-50 to-blue-100 cursor-wait opacity-60' 
                        : isSelected
                        ? 'bg-blue-100 border-l-4 border-blue-500'
                        : 'hover:bg-blue-50 cursor-pointer'
                    }`}
                    data-search-item-id={domItemId}
                    data-search-item-type={domItemType}
                    data-search-item-source={domItemSource}
                    data-search-item-provider={domItemProvider}
                    data-search-item-is-cached={String(domItemIsCached)}
                    data-search-item-source-id={domItemSourceId}
                    data-search-item-internal-id={domItemInternalId}
                    data-search-item-spoonacular-id={domItemSpoonacularId}
                    onClick={() => !isAdding && handleRecipeSelect(recipe)}
                  >
                    <div className="flex gap-2 items-center relative">
                          {recipe.image && (
                        <div className="relative">
                            <img 
                              src={recipe.image} 
                              alt={recipe.title}
                            className={`w-12 h-12 object-cover rounded transition-opacity ${isAdding ? 'opacity-40' : ''}`}
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                          {isAdding && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                            </div>
                          )}
                        </div>
                      )}
                      {!recipe.image && isAdding && (
                        <div className="w-12 h-12 flex items-center justify-center">
                          <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                        </div>
                          )}
                          <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                            <p className="text-xs font-medium text-gray-900 truncate">
                              {recipe.title}
                            {isAdding && (
                              <span className="ml-2 inline-flex items-center gap-1 text-blue-600 font-semibold">
                                <span className="inline-block w-1 h-1 bg-blue-600 rounded-full animate-pulse"></span>
                                <span className="inline-block w-1 h-1 bg-blue-600 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></span>
                                <span className="inline-block w-1 h-1 bg-blue-600 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></span>
                              </span>
                            )}
                            </p>
                        </div>
                            <div className="flex gap-2 mt-0.5 text-xs text-gray-600">
                          <span>Kcal: {formatNumber(recipe.calories || 0)}</span>
                          <span>P: {formatNumber(recipe.protein || 0)}g</span>
                          <span>C: {formatNumber(recipe.carbs || 0)}g</span>
                          <span>F: {formatNumber(recipe.fat || 0)}g</span>
                            </div>
                            <RecipeAllergenCard
                              allergens={(recipe as any).allergens}
                              compact
                              maxVisible={3}
                              className="mt-1"
                            />
                            <SpoonacularTagSummary item={recipe} className="mt-1" />
                            {Array.isArray((recipe as any).clinicalWarnings) && (recipe as any).clinicalWarnings.map((warning: any, warningIndex: number) => (
                              <div
                                key={`${String(warning?.ruleId || 'clinical-warning')}-${warningIndex}`}
                                className={`mt-1 flex items-start gap-1 rounded px-2 py-1 text-xs leading-4 ${warning?.severity === 'blocking' ? 'border border-red-300 bg-red-50 text-red-900' : 'border border-amber-300 bg-amber-50 text-amber-900'}`}
                              >
                                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                <span>{warning?.severity === 'blocking' ? 'Blocked: ' : ''}{String(warning?.message || 'Review this item for the client’s clinical condition.')}</span>
                              </div>
                            ))}
                            {isRecipeResult && (
                              <div className="mt-1 space-y-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gray-500 whitespace-nowrap">Servings (per consumption)</span>
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button
                                        type="button"
                                        className="inline-flex items-center text-gray-500 hover:text-gray-700"
                                        aria-label="What is per consumption serving?"
                                        onClick={(event) => event.stopPropagation()}
                                        onMouseDown={(event) => event.stopPropagation()}
                                      >
                                        <Info className="h-3.5 w-3.5" />
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent align="start" className="w-80 text-xs leading-relaxed">
                                      <p>
                                        Consumption servings are the portions each person eats and the nutrition values are shown for this amount.
                                      </p>
                                      <p className="mt-2">
                                        Ingredient servings are the total recipe yield prepared from all ingredients.
                                      </p>
                                      <p className="mt-2">
                                        Example: a recipe cooked for 4 people has ingredient yield = 4 servings. One person&apos;s plate is 1 consumption serving, so nutrition is shown for that 1 serving.
                                      </p>
                                    </PopoverContent>
                                  </Popover>
                                  <div
                                    className="w-24"
                                    onClick={(event) => event.stopPropagation()}
                                    onMouseDown={(event) => event.stopPropagation()}
                                  >
                                    <Select
                                      value={String(selectedServing)}
                                      onValueChange={(value) => {
                                        const parsed = parseRecipeServingValue(value);
                                        if (parsed === null) return;
                                        const resultId = String(recipe.id || '').trim();
                                        if (!resultId) return;
                                        setRecipeSearchSelectedServings((prev) => ({
                                          ...prev,
                                          [resultId]: parsed,
                                        }));
                                      }}
                                    >
                                      <SelectTrigger className="h-7 text-xs">
                                        <SelectValue placeholder="Select" />
                                      </SelectTrigger>
                                      <SelectContent data-search-dropdown-interactive="true">
                                        {allowedServings.map((servingValue) => (
                                          <SelectItem key={`${recipe.id}-serving-${servingValue}`} value={String(servingValue)}>
                                            {formatServingOptionLabel(servingValue)}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                                {availableServingSizes.length > 0 && selectedServingSizeId && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500 whitespace-nowrap">Serving size</span>
                                    <div
                                      className="w-32"
                                      onClick={(event) => event.stopPropagation()}
                                      onMouseDown={(event) => event.stopPropagation()}
                                    >
                                      <Select
                                        value={selectedServingSizeId}
                                        onValueChange={(value) => {
                                          const parsedServingId = parseRecipeServingId(value);
                                          if (!parsedServingId) return;
                                          const resultId = String(recipe.id || '').trim();
                                          if (!resultId) return;
                                          setRecipeSearchSelectedServingIds((prev) => ({
                                            ...prev,
                                            [resultId]: parsedServingId,
                                          }));
                                        }}
                                      >
                                        <SelectTrigger className="h-7 text-xs">
                                          <SelectValue placeholder="Select" />
                                        </SelectTrigger>
                                        <SelectContent data-search-dropdown-interactive="true">
                                          {availableServingSizes.map((entry) => (
                                            <SelectItem key={`${recipe.id}-size-${entry.servingId}`} value={entry.servingId}>
                                              {formatUnitForDisplay(entry.label || entry.servingId)} ({formatNumber(entry.grams)}g)
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    {selectedServingSize?.grams != null && (
                                      <span className="text-xs text-gray-500">{selectedServingSize.grams}g</span>
                                    )}
                                  </div>
                                )}
                                {showCustomYieldNote && (
                                  <div className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs leading-relaxed text-amber-900">
                                    Recipe yield is {formatNumber(totalYieldServings)} servings. For one person, use about {perPersonShare} {selectedServingSizeLabel}.
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="hidden" data-search-item-metadata="true" aria-hidden="true">
                          <span data-meta-key="id">{domItemId}</span>
                          <span data-meta-key="type">{domItemType}</span>
                          <span data-meta-key="source">{domItemSource}</span>
                          <span data-meta-key="provider">{domItemProvider}</span>
                          <span data-meta-key="isCached">{String(domItemIsCached)}</span>
                          <span data-meta-key="sourceId">{domItemSourceId}</span>
                          <span data-meta-key="internalId">{domItemInternalId}</span>
                          <span data-meta-key="spoonacularId">{domItemSpoonacularId}</span>
                        </div>
                      </li>
                );
              }}
            />
          </div>
        )}
        <IngredientSearchModal
          isOpen={isIngredientModalOpen}
          onClose={() => setIsIngredientModalOpen(false)}
          currentIngredient=""
          onReplaceIngredient={handleReplaceIngredientNoop}
          onAddIngredient={handleAddIngredientFromModal}
          isReplacing={false}
          allergies={allergies}
          clientId={clientId}
        />
        <ManualPlanConfirmDialog
          open={isDeleteMealSlotConfirmOpen}
          onOpenChange={setIsDeleteMealSlotConfirmOpen}
          title="Delete meal slot?"
          description={`Are you sure you want to delete the "${slot.name}" meal slot? This will remove all recipes in this slot.`}
          confirmText="Delete"
          loading={isDeletingSlot}
          onConfirm={handleDeleteMealSlotConfirm}
        />

        {/* Food Items */}
        {slot.items.length === 0 ? (
          <div className="text-gray-500 text-sm py-4 text-center border border-dashed rounded-md">
            No items added yet
          </div>
        ) : (
          <ul className="space-y-2">
            {slot.items.map((food, index) => {
              const state = recipeStates[index];
              
              // Check if dietary preferences are missing from health labels
              const getDietaryMismatch = () => {
                if (!dietaryPreferences || dietaryPreferences.length === 0) return null;
                if (!food.healthLabels || food.healthLabels.length === 0) return null;
                
                // Find dietary preferences that are missing from health labels
                const missingPreferences = dietaryPreferences.filter(pref => 
                  !food.healthLabels.some((label: string) => 
                    label.toLowerCase().includes(pref.toLowerCase()) || 
                    pref.toLowerCase().includes(label.toLowerCase())
                  )
                );
                
                return missingPreferences.length > 0 ? missingPreferences : null;
              };
              
              const missingPreferences = getDietaryMismatch();
              const missingPreferenceMessages = missingPreferences
                ? missingPreferences
                    .map(formatMissingPreferenceMessage)
                    .filter((message, index, self) =>
                      self.findIndex(item => item.key === message.key) === index
                    )
                : [];
              const isIngredient = food.category === 'ingredient';
              const baseAmount = getFoodBaseAmount(food);
              const baseUnit = food.servingId ?? 'g';
              const currentAmount = getFoodCurrentAmount(food, index);
              const selectedUnit = ingredientUnitOverrides[index] ?? food.servingId ?? 'g';
              const unitsForSelect = ingredientUnits[index]?.length
                ? ingredientUnits[index]
                : [selectedUnit].filter(Boolean);
              const isSavingIngredient = updatingIngredientUnitIndex === index;
              const isLoadingIngredientUnits = loadingIngredientUnitsIndex === index;
              const hasPendingIngredientChanges =
                isIngredient &&
                (Math.abs(currentAmount - baseAmount) > 0.0001 || selectedUnit !== baseUnit);
              const recipeServingSizes = !isIngredient
                ? (Array.isArray(food.servingSizes) ? food.servingSizes : [])
                : [];
              const defaultRecipeServingSizeId = recipeServingSizes.find((entry) => entry.isDefault)?.servingId
                ?? recipeServingSizes[0]?.servingId
                ?? null;
              const selectedRecipeServingId = !isIngredient
                ? (
                  parseRecipeServingId(foodServingSizeIds[index])
                  ?? parseRecipeServingId(food.selectedServingId)
                  ?? parseRecipeServingId(food.defaultServingId)
                  ?? parseRecipeServingId(defaultRecipeServingSizeId)
                )
                : null;
              const selectedRecipeServing = selectedRecipeServingId
                ? recipeServingSizes.find((entry) => parseRecipeServingId(entry.servingId) === selectedRecipeServingId)
                : null;
              const isAmountBusy = isIngredient ? isSavingIngredient : updatingServingsIndex === index;
              const displayProvider = food.provider ?? (food.source ? normalizeManualProvider(undefined, food.source) : undefined);
              const totalYieldServings = Number(food.servings);
              const showSelectedCustomYieldNote = !isIngredient
                && displayProvider === 'internal'
                && Number.isFinite(totalYieldServings)
                && totalYieldServings > 1
                && !!selectedRecipeServing;
              const perPersonShare = formatPerPersonShare(totalYieldServings);
              const selectedServingSizeLabel = formatUnitForDisplay(selectedRecipeServing?.label || selectedRecipeServing?.servingId || 'serving size');
              const displayNutrition = getDisplayNutrition(food, index);
              
              return (
                <li key={`${food.id}-${index}`} className={`flex justify-between items-center p-2 rounded-md ${missingPreferenceMessages.length > 0 ? 'bg-orange-50 border border-orange-200' : 'bg-muted/30'}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                    <p className="font-medium">
                      {food.name}
                    
                    </p>
                      {missingPreferenceMessages.length > 0 && (
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-1 text-xs text-orange-600 font-medium">
                                <div className="w-2 h-2 bg-orange-500 rounded-full" />
                                <span>
                                  {missingPreferenceMessages.map(message => message.label).join(', ')}
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <div className="space-y-1">
                                {missingPreferenceMessages.map(message => (
                                  <p key={message.key} className="text-sm leading-snug text-muted-foreground">
                                    {message.tooltip}
                                  </p>
                                ))}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-3 text-xs text-gray-600">
                      <span>Calories: {formatNumber(displayNutrition.calories)}</span>
                      <span>Protein: {formatNumber(displayNutrition.protein)}g</span>
                      <span>Total Fat: {formatNumber(displayNutrition.fat)}g</span>
                      <span>Carbohydrate: {formatNumber(displayNutrition.carbs)}g</span>
                    </div>
                    {/* Servings / Amount Editor - for both recipes and ingredients */}
                    {draftId && (
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Users className="h-3 w-3 text-gray-500" />
                        <span className="text-xs text-gray-600 flex items-center gap-1">
                          {food.category === 'ingredient' ? 'Qty:' : 'Servings:'}
                          {(updatingServingsIndex === index || isSavingIngredient) && (
                            <Loader2 className="h-2 w-2 animate-spin text-gray-600" />
                          )}
                        </span>
                        <div className="flex items-center gap-1 bg-white rounded-md border border-gray-300 px-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDecreaseServings(index)}
                            className="h-5 w-5 p-0 hover:bg-gray-100"
                            disabled={isAmountBusy || (currentAmount || 1) <= 0.1}
                            title="Decrease servings"
                          >
                            <Minus className="h-2 w-2" />
                          </Button>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={foodServingsInput[index] ?? formatNumber(currentAmount)}
                            onChange={(e) => handleTypedAmountChange(index, e.target.value)}
                            onBlur={() => commitTypedAmount(index)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                commitTypedAmount(index);
                              }
                            }}
                            disabled={isAmountBusy}
                            className="h-5 w-12 border-0 bg-transparent p-0 text-center text-xs font-medium text-gray-900 outline-none disabled:opacity-60"
                            aria-label={food.category === 'ingredient' ? 'Qty' : 'Servings'}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleIncreaseServings(index)}
                            className="h-5 w-5 p-0 hover:bg-gray-100"
                            disabled={isAmountBusy || (food.category !== 'ingredient' && (currentAmount || 1) >= 20)}
                            title="Increase servings"
                          >
                            <Plus className="h-2 w-2" />
                          </Button>
                        </div>
                        {!isIngredient && recipeServingSizes.length > 0 && selectedRecipeServingId && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-600 flex items-center gap-1">
                              Serving size:
                              <RecipeServingInfoPopover
                                defaultServingId={food.defaultServingId}
                                defaultServingGrams={food.defaultServingGrams}
                                selectedServingId={selectedRecipeServingId}
                                selectedServingGrams={food.selectedServingGrams ?? selectedRecipeServing?.grams}
                                servingSizes={recipeServingSizes}
                                className="inline-flex shrink-0 items-center text-gray-400 hover:text-gray-600"
                                iconClassName="h-3 w-3"
                              />
                            </span>
                            <Select
                              value={selectedRecipeServingId}
                              onValueChange={(value) => void handleRecipeServingSizeChange(index, value)}
                              disabled={updatingServingsIndex === index}
                            >
                              <SelectTrigger className="h-7 min-w-[140px] text-xs bg-white">
                                <SelectValue placeholder="Select serving size" />
                              </SelectTrigger>
                              <SelectContent>
                                {recipeServingSizes.map((entry) => {
                                  const normalizedServingId = parseRecipeServingId(entry.servingId);
                                  if (!normalizedServingId) return null;
                                  return (
                                    <SelectItem key={`${food.id}-size-${normalizedServingId}`} value={normalizedServingId} className="text-xs">
                                      {formatUnitForDisplay(entry.label || normalizedServingId)} ({formatNumber(entry.grams)}g)
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                            {selectedRecipeServing?.grams != null && (
                              <span className="text-xs text-gray-600">{selectedRecipeServing.grams}g</span>
                            )}
                          </div>
                        )}
                        {showSelectedCustomYieldNote && (
                          <div className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs leading-relaxed text-amber-900">
                            Recipe yield is {formatNumber(totalYieldServings)} servings. For one person, use about {perPersonShare} {selectedServingSizeLabel}.
                          </div>
                        )}
                        {isIngredient && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-600">Unit:</span>
                            <Select
                              value={selectedUnit}
                              onOpenChange={(open) => {
                                if (open) loadIngredientUnits(food, index);
                              }}
                              onValueChange={(newUnit) => handleIngredientUnitChange(index, newUnit)}
                              disabled={isSavingIngredient}
                            >
                              <SelectTrigger className="h-7 min-w-[110px] text-xs bg-white">
                                <SelectValue placeholder="Select unit" />
                              </SelectTrigger>
                              <SelectContent>
                                {(ingredientUnitOptions[index]?.length
                                  ? ingredientUnitOptions[index].map((opt) => (
                                      <SelectItem key={opt.value} value={opt.value} className="text-xs">
                                        {opt.label}
                                      </SelectItem>
                                    ))
                                  : unitsForSelect.map((unitOption) => (
                                      <SelectItem key={unitOption} value={unitOption} className="text-xs">
                                        {formatUnitForDisplay(unitOption)}
                                      </SelectItem>
                                    ))
                                )}
                                {isLoadingIngredientUnits && (
                                  <SelectItem value="loading-units" disabled className="text-xs">
                                    Loading...
                                  </SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                            {(isSavingIngredient || hasPendingIngredientChanges) && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="ml-1 h-7 px-2 text-xs"
                                onClick={() => handleSaveIngredientChanges(index)}
                                disabled={isSavingIngredient}
                              >
                                {isSavingIngredient ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  'Save'
                                )}
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Item Provider */}
                    {displayProvider && (
                      <div className="mt-1">
                        <Tag variant="neutral" size="sm">
                          {formatRecipeSource(displayProvider)}
                        </Tag>
                      </div>
                    )}
                    {food.category === 'recipe' && (
                      <RecipeAllergenCard
                        allergens={(food as any).allergens}
                        compact
                        maxVisible={3}
                        className="mt-2 max-w-md"
                      />
                    )}
                  </div>
                  <div className="flex gap-1">
                    {state === 'loading' ? (
                      // Show loader while API call is in progress
                      <div className="flex items-center px-3">
                        <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                      </div>
                    ) : state === 'error' ? (
                      // Show retry button if API call failed
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleRetryRecipe(food, index)}
                        title="Retry saving recipe"
                        className="text-red-600 hover:text-red-700 border-red-300"
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Retry
                      </Button>
                    ) : (
                      // Show normal edit/delete buttons after success or by default (no pencil for ingredients)
                      <>
                        {food.category === 'recipe' && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleEditFood(food, index)}
                            title="Edit food item"
                            disabled={isDeletingAny || deletingIndex === index}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={async () => {
                            // Prevent multiple deletions
                            if (isDeletingAny) {
                              return;
                            }
                            
                            // If draftId exists, make API call to remove item (recipe or ingredient)
                            if (draftId) {
                              setDeletingIndex(index);
                              setIsDeletingAny(true);

                              try {
                                const response = await removeManualMealPlanItem({
                                  draftId,
                                  day: selectedDay,
                                  mealName: slot.name,
                                  itemType: food.category === 'recipe' ? 'recipe' : 'ingredient',
                                  itemId: food.id,
                                });

                                if (response.success) {
                                  if (response.data && onDraftDataUpdate) {
                                    onDraftDataUpdate(response.data);
                                  }
                                  onRemoveFood(index, selectedDay, slot.type, slot.stateKey);
                                  setRecipeStates(prev => {
                                    const newState = { ...prev };
                                    delete newState[index];
                                    return newState;
                                  });
                                } else {
                                  throw new Error(response.message || 'Failed to remove item');
                                }
                              } catch (error) {
                                console.error('Error removing item:', error);
                                showErrorToast(error, 'Error Removing Item');
                              } finally {
                                setDeletingIndex(null);
                                setIsDeletingAny(false);
                              }
                            } else {
                              onRemoveFood(index, selectedDay, slot.type, slot.stateKey);
                              setRecipeStates(prev => {
                                const newState = { ...prev };
                                delete newState[index];
                                return newState;
                              });
                            }
                          }}
                          title={isDeletingAny ? "Please wait for current deletion to complete" : "Remove food item"}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          disabled={isDeletingAny}
                        >
                          {deletingIndex === index ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      {/* Recipe Details Modal */}
      {selectedRecipe && (
        <RecipeDetailsModal
          recipeId={selectedRecipe.id}
          recipeName={selectedRecipe.name}
          open={isRecipeModalOpen}
          onOpenChange={setIsRecipeModalOpen}
          draftId={draftId}
          day={selectedDay}
          mealName={slot.name}
          mealOrder={mealOrder}
          source={selectedRecipe.source || 'spoonacular'}
          onClose={handleCloseRecipeModal}
        />
      )}
    </Card>
  );
};

export default MealSlotCard;
